import React, { useEffect, useState } from 'react';
import { FaSave, FaUndo } from 'react-icons/fa';
import CheckPermission from '../ModulesRoles/CheckPermission/CheckPermission';
import { supabase } from '../../../../config/supabaseConfig.ts';
import { toastError, toastSuccess } from '../../../../utils/toast';

// Configuración de Avisos de IA (Fase 2): qué avisos genera ai-analyst para el proyecto,
// con qué umbrales y por qué canales se entregan. Se guarda en insight_settings.detectors /
// channels (solo el admin escribe, RLS). Solo se guardan los valores distintos al default,
// así un cambio de default en SQL (10_insights.sql / 14_insights_leads_calidad.sql) aplica
// a los proyectos que no lo tocaron.

// Defaults: deben coincidir con insight_detect (10) e insight_detect_leads (14).
const DETECTORS = [
  {
    group: 'Actividad',
    items: [
      {
        key: 'volume', label: 'Mensajes de clientes por canal',
        help: 'Avisa cuando suben o bajan los mensajes de clientes de un canal. Los dos umbrales aplican también a contactos nuevos y leads nuevos.',
        fields: [
          { key: 'min_base', label: 'Mínimo por semana para comparar', def: 10, min: 1, max: 100000 },
          { key: 'min_pct', label: 'Cambio mínimo (%)', def: 30, min: 1, max: 100 },
        ],
      },
      { key: 'new_contacts', label: 'Contactos nuevos', help: 'Avisa cuando cambia la cantidad de contactos nuevos.', fields: [] },
      { key: 'human_alerts', label: 'Chats esperando atención humana', help: 'Avisa cuando hay chats con alerta de atención humana.',
        fields: [{ key: 'min', label: 'Avisar desde (chats)', def: 3, min: 1, max: 1000 }] },
    ],
  },
  {
    group: 'Leads',
    note: 'Solo aplican si el proyecto tiene el módulo Leads.',
    items: [
      { key: 'leads', label: 'Leads nuevos', help: 'Avisa cuando cambia la cantidad de leads nuevos.', fields: [] },
      {
        key: 'lead_quality', label: 'Calidad de leads nuevos',
        help: 'Avisa cuando la calificación promedio de la semana sube o baja contra las 4 semanas anteriores.',
        fields: [
          { key: 'min_leads', label: 'Mínimo de leads calificados', def: 5, min: 1, max: 10000 },
          { key: 'min_pts', label: 'Cambio mínimo (puntos)', def: 10, min: 1, max: 100 },
        ],
      },
      {
        key: 'lead_hot', label: 'Leads calientes sin atender',
        help: 'Avisa cuando hay leads con calificación alta que siguen como Nuevo.',
        fields: [
          { key: 'min_score', label: 'Calificación desde (0-100)', def: 70, min: 0, max: 100 },
          { key: 'hours', label: 'Horas sin atender', def: 24, min: 1, max: 720 },
          { key: 'min', label: 'Avisar desde (leads)', def: 1, min: 1, max: 1000 },
        ],
      },
      {
        key: 'lead_weak_point', label: 'Punto débil de los leads',
        help: 'Avisa cuando una sub-calificación (presupuesto, autoridad, tiempo…) queda muy por debajo de las demás.',
        fields: [
          { key: 'min_leads', label: 'Mínimo de leads calificados', def: 5, min: 1, max: 10000 },
          { key: 'gap_pct', label: 'Por debajo del resto (%)', def: 30, min: 1, max: 100 },
        ],
      },
      {
        key: 'lead_quality_channel', label: 'Calidad de leads por canal',
        help: 'Avisa cuando los leads de un canal califican muy por debajo de los de otro (últimas 4 semanas).',
        fields: [
          { key: 'min_leads', label: 'Mínimo de leads por canal', def: 5, min: 1, max: 10000 },
          { key: 'min_gap', label: 'Diferencia mínima (puntos)', def: 15, min: 1, max: 100 },
        ],
      },
    ],
  },
];

const ALL_ITEMS = DETECTORS.flatMap((g) => g.items);

// in_app (campana del dashboard) siempre va; los externos necesitan su workflow en n8n.
const EXTERNAL_CHANNELS = [
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'email', label: 'Correo' },
];

const inputStyle = { backgroundColor: '#1a1b30', border: '1px solid rgba(255,255,255,0.08)', color: '#fff' };

// Lleva la config guardada a valores de formulario (con defaults donde no hay valor).
const toForm = (detectors) => {
  const form = {};
  ALL_ITEMS.forEach((d) => {
    const saved = detectors?.[d.key] || {};
    form[d.key] = { enabled: saved.enabled !== false };
    d.fields.forEach((f) => { form[d.key][f.key] = saved[f.key] ?? f.def; });
  });
  return form;
};

const InsightsConfig = ({ projectId }) => {
  const [form, setForm] = useState(() => toForm({}));
  const [savedDetectors, setSavedDetectors] = useState({});
  const [channels, setChannels] = useState(['in_app']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      const { data, error } = await supabase
        .from('insight_settings').select('detectors, channels').eq('project_id', projectId).maybeSingle();
      if (error) {
        toastError('No se pudo cargar la configuración de avisos (¿corriste 10_insights.sql?)');
        setLoading(false);
        return;
      }
      setSavedDetectors(data?.detectors || {});
      setForm(toForm(data?.detectors || {}));
      setChannels(data?.channels?.length ? data.channels : ['in_app']);
      setLoading(false);
    })();
  }, [projectId]);

  if (!projectId) {
    return (
      <p className="text-xs" style={{ color: '#3E4460' }}>
        Guarda el proyecto primero; después podrás configurar sus avisos.
      </p>
    );
  }
  if (loading) return <p className="text-xs" style={{ color: '#3E4460' }}>Cargando…</p>;

  const setValue = (det, field, value) =>
    setForm((prev) => ({ ...prev, [det]: { ...prev[det], [field]: value } }));

  const toggleChannel = (key) =>
    setChannels((prev) => (prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]));

  const handleSave = async () => {
    // Conserva claves que esta pantalla no maneja; de las que sí, guarda solo lo distinto al default.
    const detectors = { ...savedDetectors };
    for (const d of ALL_ITEMS) {
      const v = form[d.key];
      const out = {};
      if (!v.enabled) out.enabled = false;
      for (const f of d.fields) {
        const n = String(v[f.key]).trim() === '' ? NaN : Number(v[f.key]);
        if (!Number.isInteger(n) || n < f.min || n > f.max) {
          toastError(`${d.label}: "${f.label}" debe ser un número entero entre ${f.min} y ${f.max}`);
          return;
        }
        if (n !== f.def) out[f.key] = n;
      }
      if (Object.keys(out).length) detectors[d.key] = out;
      else delete detectors[d.key];
    }
    const nextChannels = ['in_app', ...channels.filter((c) => c !== 'in_app')];

    setSaving(true);
    const { error } = await supabase
      .from('insight_settings')
      .upsert({ project_id: projectId, detectors, channels: nextChannels, updated_at: new Date().toISOString() },
        { onConflict: 'project_id' });
    setSaving(false);
    if (error) { toastError('No se pudo guardar la configuración de avisos'); return; }
    setSavedDetectors(detectors);
    setChannels(nextChannels);
    toastSuccess('Configuración de avisos guardada. Aplica desde el próximo análisis.');
  };

  const isDefault = (d) =>
    form[d.key].enabled && d.fields.every((f) => Number(form[d.key][f.key]) === f.def);

  return (
    <div className="space-y-5 mt-3">
      {DETECTORS.map((group) => (
        <div key={group.group} className="space-y-2">
          <p className="text-xs font-semibold" style={{ color: '#8A90AD' }}>
            {group.group}
            {group.note && <span className="font-normal" style={{ color: '#4A5270' }}> — {group.note}</span>}
          </p>

          {group.items.map((d) => {
            const v = form[d.key];
            return (
              <div key={d.key} className="rounded-xl p-3 space-y-2"
                style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-start gap-3">
                  <CheckPermission label={v.enabled ? 'Activo' : 'Apagado'} checked={v.enabled}
                    onChange={(e) => setValue(d.key, 'enabled', e.target.checked)} />
                  <div className="flex-1">
                    <p className="text-sm font-semibold" style={{ color: v.enabled ? '#C9CCE0' : '#5A6278' }}>{d.label}</p>
                    <p className="text-xs" style={{ color: '#6B7194' }}>{d.help}</p>
                  </div>
                  {!isDefault(d) && (
                    <button type="button" className="text-xs flex items-center gap-1 whitespace-nowrap" style={{ color: '#00C2FF' }}
                      title="Volver a los valores por defecto"
                      onClick={() => setForm((prev) => ({ ...prev, [d.key]: toForm({})[d.key] }))}>
                      <FaUndo /> Default
                    </button>
                  )}
                </div>

                {v.enabled && d.fields.length > 0 && (
                  <div className="flex flex-wrap gap-3 pl-1">
                    {d.fields.map((f) => (
                      <label key={f.key} className="flex flex-col gap-1 text-[11px]" style={{ color: '#6B7194' }}>
                        {f.label}
                        <input
                          type="number" min={f.min} max={f.max} step={1}
                          className="rounded-lg px-3 py-1.5 text-sm w-40" style={inputStyle}
                          value={v[f.key]}
                          onChange={(e) => setValue(d.key, f.key, e.target.value)}
                        />
                        <span style={{ color: '#3E4460' }}>Por defecto: {f.def}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <div className="space-y-2">
        <p className="text-xs font-semibold" style={{ color: '#8A90AD' }}>Entrega</p>
        <div className="flex flex-row flex-wrap gap-2">
          <CheckPermission label="Dashboard (campana)" checked onChange={() => {}} />
          {EXTERNAL_CHANNELS.map((c) => (
            <CheckPermission key={c.key} label={c.label} checked={channels.includes(c.key)}
              onChange={() => toggleChannel(c.key)} />
          ))}
        </div>
        <p className="text-[11px]" style={{ color: '#4A5270' }}>
          El dashboard siempre recibe los avisos. WhatsApp y Correo dejan cada aviso en cola; se envían solo si existe el workflow de n8n de ese canal.
        </p>
      </div>

      <button
        type="button" onClick={handleSave} disabled={saving}
        className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white"
        style={{ backgroundColor: '#DC1F26', opacity: saving ? 0.6 : 1 }}
      >
        <FaSave /> {saving ? 'Guardando…' : 'Guardar configuración de avisos'}
      </button>
    </div>
  );
};

export default InsightsConfig;
